/* eslint-disable */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { stationDistanceKmFromUser } from '../../utils/stationDistanceKm';
import { cityFromStationAddress, findUkraineRegionName, stationAddressLabel } from '../../utils/ukraineRegions';
import DistanceFromYouLabel from '../../components/DistanceFromYouLabel';
import OpenMapsConfirmModal from '../../components/OpenMapsConfirmModal';
import { buildGoogleMapsNavigationUrl } from '../homePage/mapPortSearchUtils';

function formatTimeAgo(dateString, t) {
  if (!dateString) return '';
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) {
    return `${diffSecs} ${t('seconds')}`;
  }
  if (diffMins < 60) {
    return `${diffMins} ${t('minutes')}`;
  }
  if (diffHours < 24) {
    return `${diffHours} ${t('hours')}`;
  }
  const lang = localStorage.getItem('i18nextLng') || 'ua';
  let daysText = t('days');
  if (lang === 'ua') {
    if (diffDays === 1) {
      daysText = 'день';
    } else if (diffDays >= 2 && diffDays <= 4) {
      daysText = 'дні';
    } else {
      daysText = 'днів';
    }
  }
  return `${diffDays} ${daysText}`;
}

/** Port region, distance to station, online/offline — between Layout back / refresh */
export default function StartPageTopOnlineStatus({ station }) {
  const { t, i18n } = useTranslation();
  const [mapsModalOpen, setMapsModalOpen] = useState(false);
  // Same as StartManyLocationHeader: tap region to reveal online/offline period.
  const [metaExpanded, setMetaExpanded] = useState(false);
  const distanceKm = useMemo(() => stationDistanceKmFromUser(station), [station]);

  const portRegionLabel = useMemo(() => {
    const stored = stationAddressLabel(station?.address);
    if (stored) {
      return stored;
    }
    if (station?.latitude == null || station?.longitude == null) {
      return null;
    }
    const lang = (i18n.language || 'ua').split('-')[0];
    const city = cityFromStationAddress(station.address);
    const region = findUkraineRegionName(station.latitude, station.longitude, lang);
    if (city && region && !region.toLowerCase().includes(city.toLowerCase())) {
      return `${city}, ${region}`;
    }
    return city || region || null;
  }, [station, i18n.language]);

  const canOpenMaps = useMemo(
    () => Boolean(buildGoogleMapsNavigationUrl(station?.latitude, station?.longitude)),
    [station?.latitude, station?.longitude]
  );

  const updatedOn = station?.onlineUpdatedOn || station?.online_updated_on;
  const onlineStatusText = updatedOn
    ? `${station?.online ? t('online') : t('offline')} ${formatTimeAgo(updatedOn, t)}`
    : null;

  if (!updatedOn && !portRegionLabel && distanceKm == null) {
    return null;
  }

  const toolbarClass =
    portRegionLabel || distanceKm != null ? 'start-page-toolbar start-page-toolbar--with-region' : 'start-page-toolbar';

  return (
    <>
      <div className={toolbarClass} aria-live="polite">
        <p
          className={`start-page-top-status__meta-line${
            metaExpanded ? ' start-page-top-status__meta-line--expanded' : ''
          }`}
        >
          {portRegionLabel ? (
            <button
              type="button"
              className="start-page-top-status__region start-page-top-status__region--link"
              onClick={() => setMetaExpanded(open => !open)}
              aria-expanded={metaExpanded}
              aria-controls={onlineStatusText ? 'start-page-online-status' : undefined}
              title={onlineStatusText ? t('startManyRegionTapForStatus', { status: onlineStatusText }) : undefined}
            >
              {portRegionLabel}
            </button>
          ) : null}
          {portRegionLabel && distanceKm != null ? (
            <span className="start-page-top-status__dot" aria-hidden="true">
              {' '}
              •{' '}
            </span>
          ) : null}
          {distanceKm != null ? (
            canOpenMaps ? (
              <button
                type="button"
                className="start-page-top-status__distance start-page-top-status__distance--link"
                onClick={() => setMapsModalOpen(true)}
                title={t('mapPortSearchRoute')}
              >
                <DistanceFromYouLabel km={distanceKm} />
              </button>
            ) : (
              <DistanceFromYouLabel km={distanceKm} className="start-page-top-status__distance" />
            )
          ) : null}
          {onlineStatusText && metaExpanded ? (
            <>
              {portRegionLabel || distanceKm != null ? (
                <span className="start-page-top-status__dot" aria-hidden="true">
                  {' '}
                  •{' '}
                </span>
              ) : null}
              <span id="start-page-online-status" className="start-online-status start-page-top-status__text">
                {onlineStatusText}
              </span>
            </>
          ) : null}
          {onlineStatusText && !metaExpanded && portRegionLabel ? (
            <span className="start-page-top-status__dot" aria-hidden="true">
              {' '}
              • …
            </span>
          ) : null}
          {onlineStatusText && !metaExpanded && !portRegionLabel ? (
            <span className="start-online-status start-page-top-status__text">{onlineStatusText}</span>
          ) : null}
        </p>
      </div>
      {canOpenMaps ? (
        <OpenMapsConfirmModal
          modalOpen={mapsModalOpen}
          setModalOpen={setMapsModalOpen}
          latitude={station?.latitude}
          longitude={station?.longitude}
          stationNumber={station?.number}
        />
      ) : null}
    </>
  );
}
